import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabaseImageUrl, supabaseSrcSet } from "@/lib/image";

interface HeroSectionProps {
  image?: string;
  video?: string;
  poster?: string;
  label?: string;
  title: string;
  subtitle?: string;
  ctaText?: string;
  ctaHref?: string;
  ctaSecondaryText?: string;
  ctaSecondaryHref?: string;
  align?: "left" | "center";
  overlay?: "left" | "center" | "bottom";
  minHeight?: string;
  children?: React.ReactNode;
}

export default function HeroSection({
  image, video, poster, label, title, subtitle,
  ctaText, ctaHref = "/contact",
  ctaSecondaryText, ctaSecondaryHref,
  align = "left", overlay = "left",
  minHeight = "100vh", children,
}: HeroSectionProps) {
  const textRef = useRef<HTMLDivElement>(null);
  // Defer mounting the hero <video> until after first paint so it never
  // competes with the LCP element (hero image or hero H1) for bandwidth.
  // HubSpot flagged 14–22s LCP on hero-video pages; this is the fix.
  const [videoReady, setVideoReady] = useState(false);
  useEffect(() => {
    // Reveal hero text on the next frame instead of after 100ms — the
    // artificial delay was pushing the H1 out of the LCP window on
    // text-hero routes like /weddings, /lifestyle, /about.
    const raf = requestAnimationFrame(() =>
      textRef.current?.classList.add("visible"),
    );
    return () => cancelAnimationFrame(raf);
  }, []);
  useEffect(() => {
    if (!video) return;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const schedule = w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 800));
    const id = schedule(() => setVideoReady(true), { timeout: 1500 });
    return () => {
      const cancel = w.cancelIdleCallback ?? window.clearTimeout;
      cancel(id as number);
    };
  }, [video]);
  // Preload the hero image so it wins the LCP race against fonts and scripts.
  useEffect(() => {
    const href = image || poster;
    if (!href) return;
    const optimized = supabaseImageUrl(href, { width: 1920, quality: 72 });
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = optimized;
    link.fetchPriority = "high";
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [image, poster]);

  const heroSrc = image ? supabaseImageUrl(image, { width: 1920, quality: 72 }) : undefined;
  const heroSrcSet = image ? supabaseSrcSet(image, 1920, 72) : undefined;
  const posterOrImage = poster || image;
  const posterSrc = posterOrImage ? supabaseImageUrl(posterOrImage, { width: 1920, quality: 72 }) : undefined;
  const posterSrcSet = posterOrImage ? supabaseSrcSet(posterOrImage, 1920, 72) : undefined;

  return (
    <section className="relative overflow-hidden flex items-end" style={{ minHeight }}>
      <div className="absolute inset-0">
        {video ? (
          <>
            {/* Always render the poster as the LCP element; the <video> mounts
                after first paint and fades over it once ready. */}
            <img
              src={posterSrc!}
              srcSet={posterSrcSet}
              sizes="100vw"
              alt={title}
              width={1920}
              height={1080}
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover"
            />
            {videoReady && (
              <video
                src={video}
                poster={posterSrc}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
          </>
        ) : (
          <img
            src={heroSrc!}
            srcSet={heroSrcSet}
            sizes="100vw"
            alt={title}
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover ken-burns"
          />
        )}
        <div
          className={
            "absolute inset-0 " +
            (overlay === "bottom"
              ? "bg-gradient-to-r from-navy via-navy/90 to-navy/70 opacity-5"
              : overlay === "center"
                ? "hero-overlay-center"
                : "hero-overlay")
          }
        />
      </div>
      <div
        ref={textRef}
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-40 fade-up visible ${align === "center" ? "text-center" : ""}`}
      >
        {label && <p className="section-label mb-4">{label}</p>}
        <h1
          className="font-display text-white font-bold leading-tight mb-6"
          style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {subtitle && (
          <p className={`font-body text-white/80 text-lg leading-relaxed mb-10 ${align === "center" ? "max-w-2xl mx-auto" : "max-w-xl"}`}>
            {subtitle}
          </p>
        )}
        {(ctaText || ctaSecondaryText) && (
          <div className={`flex flex-wrap gap-4 ${align === "center" ? "justify-center" : ""}`}>
            {ctaText && <Link to={ctaHref} className="btn-gold">{ctaText}</Link>}
            {ctaSecondaryText && ctaSecondaryHref && (
              <Link to={ctaSecondaryHref} className="btn-outline-white">{ctaSecondaryText}</Link>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}